import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

export default function NewBox() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generatedQR, setGeneratedQR] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    location: '',
    description: '',
    tags: '',
    priority: 'normal'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate QR code generation
      const qrCode = 'QR' + Math.random().toString(36).substr(2, 6).toUpperCase();
      setGeneratedQR(qrCode);
      
      setTimeout(() => {
        alert(`Koli başarıyla kaydedildi! QR Kod: ${qrCode}`);
        navigate('/boxes');
      }, 2000);
    } catch (error) {
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const predefinedTags = [
    'kış', 'yaz', 'giyim', 'elektronik', 'mutfak', 'banyo', 'yatak odası', 
    'salon', 'kitap', 'oyuncak', 'spor', 'bahçe', 'temizlik', 'dekorasyon',
    'kırtasiye', 'ayakkabı', 'çanta', 'aksesuar', 'kırılgan', 'değerli'
  ];

  const addTag = (tag: string) => {
    const currentTags = formData.tags.split(',').map(t => t.trim()).filter(t => t);
    if (!currentTags.includes(tag)) {
      setFormData({
        ...formData,
        tags: currentTags.length > 0 ? `${formData.tags}, ${tag}` : tag
      });
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md shadow-sm sticky top-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/spaces" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Alanlarım</Link>
              <Link to="/boxes" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Koli Yönetimi</Link>
              <Link to="/profile" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Profil</Link>
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link to="/boxes" className="text-white/60 hover:text-white cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Koli Yönetimi
            </Link>
            <span className="text-white/40 mx-2">/</span>
            <span className="text-orange-400">Yeni Koli Kaydet</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Yeni Koli Kaydet</h1>
          <p className="text-white/70">Eşyalarınızı dijital olarak kaydedin ve QR kod ile takip edin</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10">
              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Temel Bilgiler */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <i className="ri-information-line mr-2 text-orange-400"></i>
                      Koli Bilgileri
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/90 mb-2">
                          Koli Adı <span className="text-red-400">*</span>
                        </label>
                        <input 
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Örn: Kış Kıyafetleri, Mutfak Eşyaları"
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/90 mb-2">
                          Kategori <span className="text-red-400">*</span>
                        </label>
                        <div className="relative">
                          <select 
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white text-sm pr-8"
                          >
                            <option value="" className="bg-slate-800">Kategori Seçin</option>
                            <option value="Giyim" className="bg-slate-800">Giyim</option>
                            <option value="Ev Eşyası" className="bg-slate-800">Ev Eşyası</option>
                            <option value="Elektronik" className="bg-slate-800">Elektronik</option>
                            <option value="Kitap" className="bg-slate-800">Kitap</option>
                            <option value="Spor" className="bg-slate-800">Spor</option>
                            <option value="Oyuncak" className="bg-slate-800">Oyuncak</option>
                            <option value="Dekorasyon" className="bg-slate-800">Dekorasyon</option>
                            <option value="Diğer" className="bg-slate-800">Diğer</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 pointer-events-none"></i>
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-white/90 mb-2">
                          Konum <span className="text-red-400">*</span>
                        </label>
                        <input 
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="Örn: Depo A - Raf 3, Evimin Bodrumu, Garaj Sol Köşe"
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/90 mb-2">
                          Öncelik Seviyesi
                        </label>
                        <div className="relative">
                          <select 
                            name="priority"
                            value={formData.priority}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white text-sm pr-8"
                          >
                            <option value="low" className="bg-slate-800">Düşük</option>
                            <option value="normal" className="bg-slate-800">Normal</option>
                            <option value="high" className="bg-slate-800">Yüksek</option>
                            <option value="urgent" className="bg-slate-800">Acil</option>
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 pointer-events-none"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Açıklama */}
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Detaylı Açıklama
                    </label>
                    <textarea 
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Kolinin içeriğini detaylı şekilde açıklayın... (Hangi eşyalar var, durumları, özel notlar vb.)"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm resize-none"
                    />
                    <div className="text-xs text-white/50 mt-1">{formData.description.length}/500 karakter</div>
                  </div>

                  {/* Etiketler */}
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Etiketler
                    </label>
                    <input 
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="virgül ile ayırın: kış, giyim, mont"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm mb-3"
                    />
                    
                    {/* Önerilen Etiketler */}
                    <div>
                      <p className="text-xs text-white/60 mb-2">Önerilen etiketler:</p>
                      <div className="flex flex-wrap gap-2">
                        {predefinedTags.map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => addTag(tag)}
                            className="px-2 py-1 text-xs bg-white/10 hover:bg-orange-500/20 text-white/70 hover:text-orange-300 rounded-md transition-colors cursor-pointer border border-white/20 hover:border-orange-500/30"
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col md:flex-row gap-4 pt-6 border-t border-white/10">
                    <Link
                      to="/boxes"
                      className="flex-1 px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors text-center cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Geri Dön
                    </Link>
                    <button
                      type="submit"
                      disabled={loading || !formData.name || !formData.category || !formData.location}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line mr-2 animate-spin"></i>
                          QR Kod Oluşturuluyor...
                        </>
                      ) : (
                        <>
                          <i className="ri-qr-code-line mr-2"></i>
                          Koli Kaydet & QR Oluştur
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Preview & Info */}
          <div className="space-y-6">
            {/* QR Preview */}
            {generatedQR && (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <i className="ri-qr-code-line mr-2 text-green-400"></i>
                  Oluşturulan QR Kod
                </h3>
                <div className="bg-white p-4 rounded-lg text-center">
                  <div className="w-32 h-32 bg-black mx-auto mb-2 rounded-lg flex items-center justify-center">
                    <i className="ri-qr-code-line text-white text-4xl"></i>
                  </div>
                  <p className="text-black font-mono text-sm">{generatedQR}</p>
                </div>
                <p className="text-xs text-white/60 mt-2 text-center">QR kodu yazdırıp kolinin üzerine yapıştırın</p>
              </div>
            )}

            {/* Info Cards */}
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl shadow-lg border border-purple-500/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <i className="ri-lightbulb-line mr-2 text-yellow-400"></i>
                İpuçları
              </h3>
              <ul className="space-y-3 text-sm text-white/80">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-400 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Koli adını açık ve anlaşılır yazın</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-400 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Kategori seçimi arama kolaylığı sağlar</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-400 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Etiketler ile hızlı filtreleme yapın</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-400 mr-2 mt-1 flex-shrink-0"></i>
                  <span>Konum bilgisini detaylı verin</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 backdrop-blur-sm rounded-2xl shadow-lg border border-orange-500/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <i className="ri-information-line mr-2 text-orange-400"></i>
                QR Kod Sistemi
              </h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li>• Her koli için benzersiz QR kod</li>
                <li>• Telefonla okutarak anında erişim</li>
                <li>• Konum ve durum takibi</li>
                <li>• Hızlı arama ve filtreleme</li>
                <li>• Dijital envanter yönetimi</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Chat Support Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer">
          <i className="ri-chat-3-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}